import type { Rect } from '../../types';

export function pointInRect(x: number, y: number, r: Rect): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

export interface ButtonOptions {
  rect: Rect;
  label: string;
  enabled?: boolean;
  highlight?: boolean;
  pressed?: boolean;
}

export function drawButton(ctx: CanvasRenderingContext2D, o: ButtonOptions): void {
  const { rect, label, enabled = true, highlight = false, pressed = false } = o;
  const baseFill = highlight ? '#5a3a14' : '#2a2420';
  const fill = pressed ? '#3a2a16' : baseFill;
  const border = highlight ? '#d49a3a' : '#7a6a55';
  ctx.fillStyle = enabled ? fill : '#1a1714';
  roundRect(ctx, rect.x, rect.y, rect.w, rect.h, 8);
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = enabled ? border : '#3a3530';
  ctx.stroke();
  ctx.fillStyle = enabled ? '#f5e9c8' : '#776a5a';
  ctx.font = 'bold 16px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1);
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export interface MeterOptions {
  rect: Rect;
  value: number;
  /** Optimal zone [lo, hi] in 0-1; drawn as a highlighted band. */
  optimal?: [number, number];
  /** Warning threshold for overheating: drawn red above. */
  danger?: number;
  vertical?: boolean;
  label?: string;
  fillColor?: string;
}

export function drawMeter(ctx: CanvasRenderingContext2D, o: MeterOptions): void {
  const { rect, value, optimal, danger, vertical = true, label, fillColor = '#d4732a' } = o;
  ctx.fillStyle = '#181412';
  roundRect(ctx, rect.x, rect.y, rect.w, rect.h, 4);
  ctx.fill();

  if (optimal) {
    ctx.fillStyle = 'rgba(80, 200, 120, 0.25)';
    if (vertical) {
      const y0 = rect.y + rect.h * (1 - optimal[1]);
      const y1 = rect.y + rect.h * (1 - optimal[0]);
      ctx.fillRect(rect.x, y0, rect.w, y1 - y0);
    } else {
      const x0 = rect.x + rect.w * optimal[0];
      const x1 = rect.x + rect.w * optimal[1];
      ctx.fillRect(x0, rect.y, x1 - x0, rect.h);
    }
  }

  if (danger !== undefined) {
    ctx.fillStyle = 'rgba(220, 60, 40, 0.25)';
    if (vertical) {
      const yD = rect.y + rect.h * (1 - 1);
      const yT = rect.y + rect.h * (1 - danger);
      ctx.fillRect(rect.x, yD, rect.w, yT - yD);
    } else {
      const xD = rect.x + rect.w * danger;
      ctx.fillRect(xD, rect.y, rect.w - (xD - rect.x), rect.h);
    }
  }

  const v = Math.max(0, Math.min(1, value));
  ctx.fillStyle = fillColor;
  if (vertical) {
    const filledH = rect.h * v;
    ctx.fillRect(rect.x + 1, rect.y + rect.h - filledH, rect.w - 2, filledH);
  } else {
    ctx.fillRect(rect.x + 1, rect.y + 1, (rect.w - 2) * v, rect.h - 2);
  }

  ctx.lineWidth = 1.5;
  ctx.strokeStyle = '#7a6a55';
  roundRect(ctx, rect.x, rect.y, rect.w, rect.h, 4);
  ctx.stroke();

  if (label) {
    ctx.fillStyle = '#c9b896';
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h + 4);
  }
}
